// US-ATH-14: a guest who pressed «Become a fan» on an athlete profile comes
// back to that profile after signing in or signing up (email or Google),
// instead of the fan's default /profile. Kept in sessionStorage so it
// survives the Google redirect; it expires so a stale one never hijacks a
// later, unrelated sign-in.

const FAN_RETURN_PATH_KEY = 'arena_fan_return_path'
// After a sign-up the welcome modal opens on that profile; its «I'll do it
// later» must keep the fan there instead of the default /profile.
const WELCOME_STAY_KEY = 'arena_fan_welcome_stay'
const TTL_MS = 30 * 60 * 1000

type Stored = { path: string; at: number }

function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** Only an in-app path: «/clubs/x», never «//host» or a full URL. */
const isSafePath = (path: string): boolean =>
  path.startsWith('/') && !path.startsWith('//') && !path.includes('\\')

export function rememberFanReturnPath(path: string): void {
  if (!isSafePath(path)) {
    return
  }
  try {
    getStorage()?.setItem(
      FAN_RETURN_PATH_KEY,
      JSON.stringify({ path, at: Date.now() } satisfies Stored)
    )
  } catch {
    // sessionStorage unavailable (private mode): the fan lands on /profile.
  }
}

export function clearFanReturnPath(): void {
  try {
    getStorage()?.removeItem(FAN_RETURN_PATH_KEY)
    getStorage()?.removeItem(WELCOME_STAY_KEY)
  } catch {
    // best effort
  }
}

/** The sign-up welcome modal opens on the returned-to profile. */
export function rememberWelcomeStay(path: string): void {
  if (!isSafePath(path)) {
    return
  }
  try {
    getStorage()?.setItem(WELCOME_STAY_KEY, path)
  } catch {
    // best effort: «later» then goes to /profile as before
  }
}

/** Read once: the welcome modal should leave the fan on this page. */
export function takeWelcomeStay(): boolean {
  try {
    const storage = getStorage()
    const path = storage?.getItem(WELCOME_STAY_KEY)
    storage?.removeItem(WELCOME_STAY_KEY)
    return Boolean(path) && path === window.location.pathname
  } catch {
    return false
  }
}

/**
 * The path to come back to after auth, read once (it is cleared). Only for a
 * fan: a club, an athlete or an agent account keeps its own landing.
 */
export function takeFanReturnPath(roles: readonly string[]): string | null {
  const storage = getStorage()
  let stored: Stored | null = null
  try {
    const raw = storage?.getItem(FAN_RETURN_PATH_KEY)
    stored = raw ? (JSON.parse(raw) as Stored) : null
  } catch {
    stored = null
  }
  try {
    storage?.removeItem(FAN_RETURN_PATH_KEY)
  } catch {
    // best effort
  }
  if (
    !stored ||
    typeof stored.path !== 'string' ||
    typeof stored.at !== 'number' ||
    Date.now() - stored.at > TTL_MS ||
    !isSafePath(stored.path)
  ) {
    return null
  }
  const isFan =
    !roles.includes('USER_ROLE_CLUB') && !roles.includes('USER_ROLE_AGENT')
  return isFan ? stored.path : null
}
